import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, ChevronDown, ChevronRight, Circle, Loader2, XCircle } from "lucide-react";

import { ConversionPayload, RolePayload, SubtypePayload } from "../../DocumentAnalysisAPI";
import InstanceCards, { InstanceSummary } from "./InstanceCards";
import ResolutionCards, { ResolutionSummary } from "./ResolutionCards";

import { Button } from "@/components/ui/button";

/**
 * One step of a run: what it is doing, how long it has taken, and what it produced.
 *
 * The result is folded away by default and opened per step, because the three results are read at
 * different moments. The conversion preview matters when something looks wrong further down; the
 * two classifications are what one came for. Collapsed, the three lines are a status; expanded,
 * they are the evidence.
 */

export interface StepState {
  status: "waiting" | "running" | "done" | "failed";
  message?: string;
  progress?: number;
  elapsedMs?: number;
  payload?: any;
}

/** How many lines of the converted text are shown before "vollständig anzeigen". */
const PREVIEW_LINES = 200;

const icon = (status: StepState["status"]) => {
  if (status === "running") return <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden />;
  if (status === "done") return <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden />;
  if (status === "failed") return <XCircle className="h-4 w-4 text-destructive" aria-hidden />;

  return <Circle className="h-4 w-4 text-muted-foreground/40" aria-hidden />;
};

const seconds = (elapsedMs?: number) =>
  typeof elapsedMs === "number" ? `${(elapsedMs / 1000).toFixed(1)} s` : "";

export default function StepRow({ step, state }: { step: string; state: StepState }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const expandable = state.status === "done" && !!state.payload;

  return (
    <li className="rounded-md border">
      {/* Top-aligned, not centred: the extraction row grows to the length of its list, and an icon
          drifting to the middle of it would no longer sit beside the step it marks. */}
      <div className="flex items-start gap-3 p-3">
        <span className="shrink-0 pt-0.5">{icon(state.status)}</span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-sm font-medium">{t(`documentAnalysisPanel.test.steps.${step}`)}</span>
            <span className="text-xs text-muted-foreground">
              {t(`documentAnalysisPanel.test.status.${state.status}`)}
            </span>
            {state.elapsedMs !== undefined ? (
              <span className="text-xs text-muted-foreground">· {seconds(state.elapsedMs)}</span>
            ) : null}
          </div>

          {state.message ? (
            <p className="truncate text-xs text-muted-foreground" title={state.message}>
              {state.message}
            </p>
          ) : null}

          {/* Only where there is a number to show. A progress bar of an unknown fraction is a
              decoration that says "I know how far along I am" when nothing does. */}
          {state.status === "running" && typeof state.progress === "number" ? (
            <div className="mt-1 h-1 w-full overflow-hidden rounded bg-muted">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${Math.round(Math.min(Math.max(state.progress, 0), 1) * 100)}%` }}
              />
            </div>
          ) : null}

          {/* The extraction is the one step whose result is a list rather than a verdict, and the
              first question about a list is what is in it. Naming the objects and their filled
              attributes here answers that without a click; the cards below stay for reading one
              closely. */}
          {step === "objectExtraction" && state.status === "done" && state.payload ? (
            <InstanceSummary payload={state.payload} />
          ) : null}

          {/* Same reason, one step on: the outcome of the resolution is a list, and the first
              question about a list is what is in it. */}
          {step === "matching" && state.status === "done" && state.payload ? (
            <ResolutionSummary payload={state.payload} />
          ) : null}
        </div>

        {expandable ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            aria-expanded={open}
            aria-label={t("documentAnalysisPanel.test.toggleResult")}
            onClick={() => setOpen(!open)}
          >
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        ) : null}
      </div>

      {open && expandable ? (
        <div className="border-t bg-muted/30 p-3">
          {step === "conversion" ? <ConversionResult payload={state.payload} /> : null}
          {step === "roleClassification" ? <RoleResult payload={state.payload} /> : null}
          {step === "subtypeClassification" ? <SubtypeResult payload={state.payload} /> : null}
          {step === "objectExtraction" ? <InstanceCards payload={state.payload} /> : null}
          {step === "matching" ? <ResolutionCards payload={state.payload} /> : null}
        </div>
      ) : null}
    </li>
  );
}

function ConversionResult({ payload }: { payload: ConversionPayload }) {
  const { t } = useTranslation();
  const [full, setFull] = useState(false);

  const lines = (payload.content ?? "").split("\n");
  const shortened = lines.length > PREVIEW_LINES && !full;
  const shown = shortened ? lines.slice(0, PREVIEW_LINES).join("\n") : payload.content;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        <span>{t(`documentAnalysisPanel.test.format.${payload.format}`)}</span>
        {payload.meta?.pages ? <span>· {t("documentAnalysisPanel.test.pages", { count: payload.meta.pages })}</span> : null}
        {payload.meta?.sheets ? <span>· {t("documentAnalysisPanel.test.sheets", { count: payload.meta.sheets })}</span> : null}
        <span>· {t("documentAnalysisPanel.test.chars", { count: payload.meta?.chars ?? 0 })}</span>
      </div>

      <pre className="max-h-[420px] overflow-auto whitespace-pre-wrap break-words rounded border bg-background p-2 text-xs">
        {shown}
      </pre>

      <div className="flex items-center gap-3">
        {lines.length > PREVIEW_LINES ? (
          <Button variant="link" size="sm" className="h-auto p-0" onClick={() => setFull(!full)}>
            {full
              ? t("documentAnalysisPanel.test.showLess")
              : t("documentAnalysisPanel.test.showAll", { lines: lines.length })}
          </Button>
        ) : null}

        {/* The server keeps the full text for the classification; what came over the wire is a
            preview, and saying so avoids reading a cut-off document as the model's input. */}
        {payload.truncated ? (
          <span className="text-xs text-muted-foreground">{t("documentAnalysisPanel.test.truncated")}</span>
        ) : null}
      </div>
    </div>
  );
}

const percent = (confidence: number) => `${Math.round((confidence ?? 0) * 100)} %`;

function RoleResult({ payload }: { payload: RolePayload }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2 text-sm">
      {payload.role ? (
        <div className="flex items-baseline gap-2">
          <span className="font-semibold">{payload.role}</span>
          <span className="text-xs text-muted-foreground">{percent(payload.confidence)}</span>
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">
          {t("documentAnalysisPanel.test.undecided")}
          {payload.undecidedReason ? <> — {payload.undecidedReason}</> : null}
        </div>
      )}

      {payload.alternatives?.length > 0 ? (
        <div className="text-xs text-muted-foreground">
          {t("documentAnalysisPanel.test.alternatives")}:{" "}
          {payload.alternatives
            .map((entry) => `${entry.role} (${percent(entry.confidence)})`)
            .join(", ")}
        </div>
      ) : null}

      {payload.rationale ? <p className="text-xs">{payload.rationale}</p> : null}
    </div>
  );
}

function SubtypeResult({ payload }: { payload: SubtypePayload }) {
  const { t } = useTranslation();

  if (payload.skipped) {
    return (
      <p className="text-sm text-muted-foreground">
        {t("documentAnalysisPanel.test.skipped", { reason: payload.reason ?? "" })}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2 text-sm">
      {payload.subtype ? (
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-semibold">{payload.subtype}</span>
          {payload.parameterValue ? <span>= {payload.parameterValue}</span> : null}
          <span className="text-xs text-muted-foreground">{percent(payload.confidence)}</span>
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">
          {t("documentAnalysisPanel.test.undecided")}
          {payload.reason ? <> — {payload.reason}</> : null}
        </div>
      )}

      {payload.rationale ? <p className="text-xs">{payload.rationale}</p> : null}
    </div>
  );
}
